import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLayoutVariant } from '../context/LayoutVariantContext';
import { toast } from '../lib/safeToast';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';

const CHART_PERIODS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '3 months' },
  { value: '180d', label: '6 months' },
  { value: '1y', label: '1 year' },
];

const Analytics = () => {
  const { api } = useAuth();
  const { isReference } = useLayoutVariant();
  const [analytics, setAnalytics] = useState(null);
  const [currencyData, setCurrencyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, currencyRes] = await Promise.all([
        api.get(`/analytics/summary?period=${period}`),
        api.get(`/analytics/charts?chart_type=currencies&period=${period}`),
      ]);
      setAnalytics(summaryRes.data);
      setCurrencyData(currencyRes.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const COLORS = ['#1C472B', '#A5E0D2', '#F08080', '#64748B', '#0EA5E9'];

  const periodLabels = {
    '7d': 'Last 7 Days',
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days',
    '180d': '6 months',
    '1y': 'Last Year',
  };

  /* Same spacing, margin, and font as Dashboard. Old look: no color/background change. */
  const pageTitleClass = isReference
    ? 'text-3xl font-bold text-[#073B2E] tracking-tight'
    : 'text-[2.25rem] font-bold leading-tight tracking-tight text-[#0F172A]';
  const pageSubtitleClass = isReference
    ? 'text-[#0E1B16] opacity-70 mt-1'
    : 'text-base font-normal leading-relaxed text-slate-500 mt-1';
  const cardClass = isReference ? 'border-[#DCE4E0] shadow-sm rounded-xl bg-white' : 'card';
  const sectionTitleClass = isReference ? 'text-xl font-bold text-[#073B2E] mb-4' : 'text-xl font-bold text-[#0F172A] mb-4';
  const cardContentClass = isReference ? 'p-6' : 'p-8';
  const cardHeaderPaddingClass = isReference ? 'px-6 pt-6 pb-2' : 'px-8 pt-8 pb-2';
  const cardContentPaddingClass = isReference ? 'px-6 pt-4 pb-6' : 'px-8 pt-6 pb-8';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="analytics-page" className="space-y-8">
        {/* Header - same as Dashboard */}
        <div className={cn('flex flex-col sm:flex-row sm:items-center justify-between', isReference ? 'gap-4 mb-8' : 'gap-4')}>
          <div className={isReference ? 'space-y-1' : 'space-y-1.5'}>
            <h1 className={pageTitleClass}>
              Analytics
            </h1>
            <p className={pageSubtitleClass}>
              Insights and trends for your account
            </p>
          </div>
          <div className={cn('flex items-center', isReference ? 'gap-3' : 'gap-4')}>
            <div className="flex flex-wrap gap-1.5">
              {CHART_PERIODS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPeriod(p.value)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    period === p.value
                      ? isReference ? 'bg-[#0B6445] text-white' : 'bg-slate-200 text-[#0F172A]'
                      : isReference ? 'bg-[#F7FAF9] text-[#0E1B16] border border-[#DCE4E0] hover:bg-[#E6F7F3] hover:border-[#0B6445]' : 'bg-slate-50 text-[#0F172A] border border-slate-200 hover:bg-slate-100'
                  )}
                  data-testid={period === p.value ? 'period-select' : undefined}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button
              onClick={fetchAnalytics}
              variant="outline"
              className={isReference ? 'h-11 px-5 rounded-lg border-[#DCE4E0] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E] font-medium' : 'h-11 px-5 rounded-full border border-[#1C472B] text-[#1C472B] hover:bg-slate-50 hover:text-[#073B2E] font-medium'}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Stats - same card/typography as Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className={cardClass} data-testid="total-inflow">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-[#E6F7F3] rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-[#1C472B]" />
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 mb-2">Total Inflow</p>
              <p className="text-2xl font-bold tracking-tight">
                <span className="text-[#1C472B]">{formatCurrency(analytics?.total_inflow || 0)}</span>
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass} data-testid="total-outflow">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
                  <TrendingDown className="w-7 h-7 text-[#F08080]" />
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 mb-2">Total Outflow</p>
              <p className="text-2xl font-bold tracking-tight">
                <span className="text-[#F08080]">{formatCurrency(analytics?.total_outflow || 0)}</span>
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass} data-testid="net-flow">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-[#E6F7F3] rounded-2xl flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-[#1C472B]" />
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 mb-2">Net Flow</p>
              <p className="text-2xl font-bold text-[#0F172A] tracking-tight">
                {formatCurrency(analytics?.net_flow || 0)}
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass} data-testid="tx-count">
            <CardContent className={cardContentClass}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                  <Activity className="w-7 h-7 text-slate-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 mb-2">Transactions</p>
              <p className="text-2xl font-bold text-[#0F172A] tracking-tight">
                {analytics?.transaction_count || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts - same card header/content padding and section title as Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className={cardClass} data-testid="volume-chart">
            <CardHeader className={cardHeaderPaddingClass}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className={sectionTitleClass}>
                    Daily Volume
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {periodLabels[period] ?? CHART_PERIODS.find((p) => p.value === period)?.label}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className={cardContentPaddingClass}>
              <div className="relative w-full min-h-[200px]" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height={320} minWidth={0}>
                  <AreaChart data={analytics?.daily_volume?.slice(-14) || []}>
                    <defs>
                      <linearGradient id="colorIn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1C472B" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#1C472B" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorOut" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F08080" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#F08080" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => value.split('-').slice(1).join('/')}
                    />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="inflow"
                      stroke="#1C472B"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorIn)"
                      name="Inflow"
                    />
                    <Area
                      type="monotone"
                      dataKey="outflow"
                      stroke="#F08080"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorOut)"
                      name="Outflow"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className={cardClass} data-testid="currency-chart">
            <CardHeader className={cardHeaderPaddingClass}>
              <CardTitle className={sectionTitleClass}>
                Currency Distribution
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">Balance by currency</p>
            </CardHeader>
            <CardContent className={cardContentPaddingClass}>
              <div className="relative w-full min-h-[200px]" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height={320} minWidth={0}>
                  <PieChart>
                    <Pie
                      data={currencyData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="balance"
                      nameKey="currency"
                      label={({ currency, percentage }) => `${currency} ${percentage}%`}
                    >
                      {currencyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className={cardClass} data-testid="currency-breakdown">
          <CardHeader className={cardHeaderPaddingClass}>
            <CardTitle className={sectionTitleClass}>
              Currency Breakdown
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">Inflow vs Outflow by currency</p>
          </CardHeader>
          <CardContent className={cardContentPaddingClass}>
            <div className="relative w-full min-h-[200px]" style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height={320} minWidth={0}>
                <BarChart
                  data={Object.entries(analytics?.currency_breakdown || {}).map(
                    ([currency, data]) => ({
                      currency,
                      inflow: parseFloat(data.inflow),
                      outflow: parseFloat(data.outflow),
                    })
                  )}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="currency" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E2E8F0',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Bar dataKey="inflow" fill="#1C472B" name="Inflow" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="outflow" fill="#F08080" name="Outflow" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Analytics;
